import { useState } from "react"
import NextLink from "next/link"
import type { PreviewContact, PreviewHousehold, PreviewTrust } from "@/schema"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import { AtSign, Building2, CalendarDays, FolderKanban, Mail, MessageSquare, PhoneCall, Shield, X } from "lucide-react"

import { householdRouteSegment } from "@/lib/household-route-segment"
import { usePeekViewData } from "@/hooks/api/peek-views/queries"
import { Link } from "@/components/book-link"

import BusinessPeekView from "./business"
import CallLogPeekView from "./call-log"
import ContactPeekView from "./contact"
import HouseholdPeekView from "./household"
import MeetingPeekView from "./meeting"
import PersonPeekView from "./person"
import ProjectPeekView from "./project"
import SMSMessagePeekView from "./sms-message"
import SyncboxMessagePeekView from "./syncbox-message"
import TrustPeekView from "./trust"

const triggerIcon = {
  Client: <AtSign className="size-3" strokeWidth={1.5} />,
  Prospect: <AtSign className="size-3" strokeWidth={1.5} />,
  PastClient: <AtSign className="size-3" strokeWidth={1.5} />,
  Household: <AtSign className="mb-px size-3.5" strokeWidth={1.5} />,
  Person: <AtSign className="mb-px size-3.5" strokeWidth={1.5} />,
  Contact: <AtSign className="mb-px size-3.5" strokeWidth={1.5} />,
  Business: <Building2 className="mb-px size-3.5" strokeWidth={1.5} />,
  Meeting: <CalendarDays className="mb-px size-3.5" strokeWidth={1.5} />,
  User: <AtSign className="mb-px size-3.5" strokeWidth={1.5} />,
  Trust: <Shield className="mb-px size-3.5" strokeWidth={1.5} />,
  "Syncbox::Message": <Mail className="mb-px size-3.5" strokeWidth={1.5} />,
  SMSMessage: <MessageSquare className="mb-px size-3.5" strokeWidth={1.5} />,
  CallLog: <PhoneCall className="mb-px size-3.5" strokeWidth={1.5} />,
  Project: <FolderKanban className="mb-px size-3.5" strokeWidth={1.5} />,
}

function PreviewTrigger({
  label,
  href,
  size,
  recordType,
  className,
  disabled = false,
  disableLink = false,
  useDirectHref = false,
}: {
  label: string
  href: string | null
  size?: "sm" | "base"
  recordType: RecordType
  className?: string
  disabled?: boolean
  disableLink?: boolean
  useDirectHref?: boolean
}) {
  const linkClasses = cn(
    "border-link/40 inline-flex max-w-full cursor-pointer items-center gap-0.5 border-b border-dashed [&>svg]:shrink-0",
    "text-link hover:border-link-hover hover:text-link-hover transition-colors duration-150",
    size === "sm" ? "text-sm/tight" : "text-base/tight",
    disabled && "cursor-default",
    className
  )

  if (!!href && !disabled && !disableLink) {
    const TriggerLink = useDirectHref ? NextLink : Link

    return (
      <TriggerLink href={href} className={linkClasses} onClick={(e) => e.stopPropagation()}>
        {triggerIcon[recordType]}
        <span className="truncate">{label}</span>
      </TriggerLink>
    )
  } else {
    return (
      <span className={linkClasses}>
        {triggerIcon[recordType]}
        <span className="truncate">{label}</span>
      </span>
    )
  }
}

type RecordType =
  | "Client"
  | "Prospect"
  | "PastClient"
  | "User"
  | "Meeting"
  | "Household"
  | "Business"
  | "Contact"
  | "Person"
  | "Trust"
  | "Syncbox::Message"
  | "SMSMessage"
  | "CallLog"
  | "Project"

function getHouseholdHref(household: PreviewHousehold): string {
  return `/${householdRouteSegment(household.type)}/${household.id}`
}

function getPersonHref(personPreview?: PreviewHousehold | PreviewContact): string | null {
  if (!personPreview) return null

  if ("name" in personPreview && "type" in personPreview) {
    return getHouseholdHref(personPreview)
  }

  return `/contacts/${personPreview.id}`
}

function PillTrigger({
  label,
  onRemove,
  className,
  href,
}: {
  label: string
  onRemove?: () => void
  className?: string
  href?: string | null
}) {
  const pillClasses = cn(
    "text-tertiary-foreground border-border bg-background inline-flex min-w-0 items-center rounded-full border px-2 py-0.5 text-xs font-medium",
    onRemove && "pr-1",
    className
  )

  const content = (
    <>
      <span className="truncate">{label}</span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onRemove()
          }}
          className="text-muted-foreground hover:text-destructive ml-0.5 shrink-0"
          aria-label={label ? `Remove ${label}` : "Remove"}
        >
          <X className="size-3" />
        </button>
      )}
    </>
  )

  if (href) {
    return (
      <Link href={href} className={pillClasses} onClick={(e) => e.stopPropagation()}>
        {content}
      </Link>
    )
  }

  return <span className={pillClasses}>{content}</span>
}

interface BasePeekViewProps {
  label?: string
  children?: React.ReactNode
  size?: "sm" | "base"
  recordType: RecordType
  recordId: string
  className?: string
  disableTriggerLink?: boolean
  hrefOverride?: string | null
  asPill?: boolean
  onRemove?: () => void
}

export default function PeekView({
  label,
  children,
  size,
  recordType,
  recordId,
  className,
  disableTriggerLink = false,
  hrefOverride,
  asPill = false,
  onRemove,
}: BasePeekViewProps) {
  const [open, setOpen] = useState(false)
  const trustPreviewQuery = usePeekViewData(recordId, "Trust", recordType === "Trust")
  const trustPreview = recordType === "Trust" ? (trustPreviewQuery.data as PreviewTrust | undefined) : undefined
  const personPreviewQuery = usePeekViewData(recordId, "Person", recordType === "Person")
  const personPreview =
    recordType === "Person" ? (personPreviewQuery.data as PreviewHousehold | PreviewContact | undefined) : undefined

  const getRoutePath = (type: string): string | null => {
    if (type === "User") return null
    if (type === "Business") return "businesses"
    if (type === "Meeting") return "meetings"
    if (type === "Contact") return "contacts"
    if (type === "Person") return "people"
    if (type === "Household") return "households"
    if (type === "Trust") return null
    if (type === "Syncbox::Message") return null
    if (type === "SMSMessage") return null
    if (type === "CallLog") return null
    if (type === "Project") return "projects"
    return householdRouteSegment(type)
  }

  const routePath = getRoutePath(recordType)
  const trustRoute =
    recordType === "Trust" && trustPreview?.household?.id
      ? `/clients/${trustPreview.household.id}/trusts/${recordId}`
      : null
  const personRoute = recordType === "Person" ? getPersonHref(personPreview) : null
  const baseHref = recordType === "Person" ? personRoute : routePath ? `/${routePath}/${recordId}` : null
  const inferredHref = recordType === "Trust" ? trustRoute : baseHref
  const href = hrefOverride === undefined ? inferredHref : hrefOverride
  const previewHref = hrefOverride === undefined ? href : null
  const isTrustDisabled = recordType === "Trust" && !trustRoute

  function renderPreview() {
    if (
      recordType === "Client" ||
      recordType === "Prospect" ||
      recordType === "PastClient" ||
      recordType === "Household"
    ) {
      return <HouseholdPeekView householdId={recordId} href={previewHref} />
    } else if (recordType === "Meeting") {
      return <MeetingPeekView meetingId={recordId} href={previewHref} />
    } else if (recordType === "Business") {
      return <BusinessPeekView businessId={recordId} href={previewHref} />
    } else if (recordType === "Contact") {
      return <ContactPeekView contactId={recordId} href={previewHref} />
    } else if (recordType === "Person") {
      return <PersonPeekView personId={recordId} href={previewHref} />
    } else if (recordType === "Trust") {
      return <TrustPeekView trustId={recordId} href={previewHref} />
    } else if (recordType === "Syncbox::Message") {
      return <SyncboxMessagePeekView syncboxMessageId={recordId} href={previewHref} />
    } else if (recordType === "SMSMessage") {
      return <SMSMessagePeekView smsMessageId={recordId} href={previewHref} />
    } else if (recordType === "CallLog") {
      return <CallLogPeekView callLogId={recordId} href={previewHref} />
    } else if (recordType === "Project") {
      return <ProjectPeekView projectId={recordId} href={previewHref} />
    }
  }

  return (
    <HoverCard open={open} onOpenChange={setOpen}>
      <HoverCardTrigger asChild>
        <span className="inline-flex max-w-full">
          {children ? (
            children
          ) : asPill ? (
            <PillTrigger label={label || ""} onRemove={onRemove} className={className} href={href} />
          ) : (
            <PreviewTrigger
              label={label || ""}
              size={size}
              href={href}
              recordType={recordType}
              className={className}
              disabled={isTrustDisabled}
              disableLink={disableTriggerLink}
              useDirectHref={hrefOverride !== undefined}
            />
          )}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-fit" align="start" onClick={(e) => e.stopPropagation()}>
        {renderPreview()}
      </HoverCardContent>
    </HoverCard>
  )
}
